import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import {
  DEFAULT_REGISTER_PREFERENCES,
  REGISTER_EDIT_SCROLL_ANCHOR_LABELS,
  REGISTER_EDIT_SCROLL_ANCHORS,
  REGISTER_LIST_DISPLAY_HELP,
  REGISTER_LIST_DISPLAY_LABELS,
  REGISTER_LIST_DISPLAY_MODES,
  REGISTER_SEARCH_MODE_HELP,
  REGISTER_SEARCH_MODE_LABELS,
  REGISTER_SEARCH_MODES,
  REGISTER_STATS_SCROLL_ANCHOR_LABELS,
  REGISTER_STATS_SCROLL_ANCHORS,
  sanitizeRegisterPreferences,
} from "../constants/registerPreferences";
import "./DeleteWordModal.css";
import "./RegisterSettingsModal.css";
import "./EntrainementVoc.css";

function RadioChip({ name, label, checked, onChange }) {
  return (
    <label
      className={`entrainement-chip entrainement-chip--round${
        checked ? " entrainement-chip--checked" : ""
      }`}
    >
      <input
        type="radio"
        className="entrainement-chip__input"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

export default function RegisterSettingsModal({
  open,
  value,
  onChange,
  onClose,
  title = "Paramètres du registre",
  showScrollPreferences = true,
  gothicTitle = true,
  searchHelpMap = null,
  namePrefix = "register",
  defaults = DEFAULT_REGISTER_PREFERENCES,
}) {
  const [draft, setDraft] = useState(defaults);

  useEffect(() => {
    if (!open) return;
    setDraft(sanitizeRegisterPreferences(value));
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleApply = () => {
    onChange?.(sanitizeRegisterPreferences(draft));
    onClose?.();
  };

  const handleReset = () => {
    onChange?.({ ...defaults });
    onClose?.();
  };

  const searchHelp = (searchHelpMap || REGISTER_SEARCH_MODE_HELP)[draft.searchMode];
  const displayHelp = REGISTER_LIST_DISPLAY_HELP[draft.listDisplayMode];

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="delete-word-modal register-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${namePrefix}-settings-modal-title`}
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h3
          id={`${namePrefix}-settings-modal-title`}
          className={`delete-word-modal__title${
            gothicTitle ? " delete-word-modal__title--gothic" : ""
          }`}
        >
          {title}
        </h3>

        <div className="register-settings-modal__body">
          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">Mode de recherche</h4>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
              role="radiogroup"
              aria-label="Mode de recherche"
            >
              {Object.values(REGISTER_SEARCH_MODES).map((mode) => (
                <RadioChip
                  key={mode}
                  name={`${namePrefix}-search-mode`}
                  label={REGISTER_SEARCH_MODE_LABELS[mode]}
                  checked={draft.searchMode === mode}
                  onChange={() => setDraft((prev) => ({ ...prev, searchMode: mode }))}
                />
              ))}
            </div>
          </section>

          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">
              Affichage à droite du mot
            </h4>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--display"
              role="radiogroup"
              aria-label="Affichage dans la liste"
            >
              {Object.values(REGISTER_LIST_DISPLAY_MODES).map((mode) => (
                <RadioChip
                  key={mode}
                  name={`${namePrefix}-list-display-mode`}
                  label={REGISTER_LIST_DISPLAY_LABELS[mode]}
                  checked={draft.listDisplayMode === mode}
                  onChange={() => setDraft((prev) => ({ ...prev, listDisplayMode: mode }))}
                />
              ))}
            </div>
          </section>

          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">Code couleur</h4>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
              role="radiogroup"
              aria-label="Code couleur"
            >
              <RadioChip
                name={`${namePrefix}-color-code`}
                label="Activé"
                checked={draft.showColorCode === true}
                onChange={() => setDraft((prev) => ({ ...prev, showColorCode: true }))}
              />
              <RadioChip
                name={`${namePrefix}-color-code`}
                label="Désactivé"
                checked={draft.showColorCode === false}
                onChange={() => setDraft((prev) => ({ ...prev, showColorCode: false }))}
              />
            </div>
          </section>

          {showScrollPreferences ? (
          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">Préférence de scroll</h4>
            <div className="register-settings-modal__scroll-group">
              <span className="register-settings-modal__scroll-label">
                Volet statistiques
              </span>
              <div
                className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
                role="radiogroup"
                aria-label="Positionnement du volet statistiques"
              >
                {Object.values(REGISTER_STATS_SCROLL_ANCHORS).map((anchor) => (
                  <RadioChip
                    key={anchor}
                    name={`${namePrefix}-stats-scroll-anchor`}
                    label={REGISTER_STATS_SCROLL_ANCHOR_LABELS[anchor]}
                    checked={draft.statsScrollAnchor === anchor}
                    onChange={() =>
                      setDraft((prev) => ({ ...prev, statsScrollAnchor: anchor }))
                    }
                  />
                ))}
              </div>
            </div>
            <div className="register-settings-modal__scroll-group">
              <span className="register-settings-modal__scroll-label">Volet édition</span>
              <div
                className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--3"
                role="radiogroup"
                aria-label="Positionnement du volet édition"
              >
                {Object.values(REGISTER_EDIT_SCROLL_ANCHORS).map((anchor) => (
                  <RadioChip
                    key={anchor}
                    name={`${namePrefix}-edit-scroll-anchor`}
                    label={REGISTER_EDIT_SCROLL_ANCHOR_LABELS[anchor]}
                    checked={draft.editScrollAnchor === anchor}
                    onChange={() =>
                      setDraft((prev) => ({ ...prev, editScrollAnchor: anchor }))
                    }
                  />
                ))}
              </div>
            </div>
          </section>
          ) : null}

        </div>

        <p className="register-settings-modal__help" aria-live="polite">
          <span className="register-settings-modal__help-line">{searchHelp}</span>
          <span className="register-settings-modal__help-line">{displayHelp}</span>
        </p>

        <div className="delete-word-modal__actions register-settings-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={handleReset}
          >
            Réinitialiser
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--confirm"
            onClick={handleApply}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
